import { useEffect, useState } from "react";
import { useNavigation } from "@/context/NavigationContext";
import { useAppContext } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext"; // 1. Import useAuth
import {
  ArrowLeft,
  FileText,
  Scale,
  Calendar,
  AlertTriangle,
  Shield,
  EyeOff,
  UserCheck,
} from "lucide-react";
import { message } from "antd";
import InstrumentLoader from "@/components/InstrumentLoader";

// Dynamic Lucide Icon Mapper
const IconMapper = ({ name, size = 14 }: { name?: string; size?: number }) => {
  switch (name) {
    case "FileText":
      return <FileText size={size} />;
    case "AlertTriangle":
      return <AlertTriangle size={size} />;
    case "Shield":
      return <Shield size={size} />;
    case "EyeOff":
      return <EyeOff size={size} />;
    case "UserCheck":
      return <UserCheck size={size} />;
    default:
      return <AlertTriangle size={size} />;
  }
};

const TermsConditions = () => {
  const { navigateTo, params } = useNavigation();
  const { termsConditions } = useAppContext();
  const { currentLang } = useAuth(); // 2. Consume currentLang ('ro' | 'en')

  const fromRegister = params?.fromRegister ?? false;

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadTerms = async () => {
      try {
        setLoading(true);
        const result = await termsConditions();
        if (result) {
          // Response is a map like { en: "{...}", ro: "{...}" } or parsed objects
          const rawLangData = result[currentLang] || result["ro"] || result;
          const parsedData =
            typeof rawLangData === "string" ? JSON.parse(rawLangData) : rawLangData;
          setData(parsedData);
        } else {
          message.error(
            currentLang === "en"
              ? "Could not load Terms & Conditions."
              : "Nu s-au putut încărca termenii și condițiile."
          );
        }
      } catch (error) {
        console.error("Eroare la parsare/incarcare termeni:", error);
      } finally {
        setLoading(false);
      }
    };

    loadTerms();
  }, [termsConditions, currentLang]);

  return (
    <div className="container py-8 md:py-12 text-white min-h-screen flex flex-col font-sans bg-[#020617]">
      {/* Loader */}
      <InstrumentLoader visible={loading} />

      {fromRegister && (
        <div className="max-w-4xl mx-auto w-full mb-6">
          <button
            onClick={() => navigateTo("register")}
            className="group inline-flex items-center gap-2 text-slate-400 hover:text-emerald-500 font-bold uppercase tracking-widest text-xs transition-colors"
          >
            <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" />
            {currentLang === "en" ? "Back to registration" : "Înapoi la înregistrare"}
          </button>
        </div>
      )}

      {/* Main Document Layout */}
      {!loading && !data ? (
        <div className="min-h-[70vh] flex flex-col items-center justify-center text-white p-6 text-center">
          <AlertTriangle className="text-red-500 mb-4" size={48} />
          <h2 className="text-xl font-bold mb-2">
            {currentLang === "en" ? "Loading Error" : "Eroare de încărcare"}
          </h2>
          <p className="text-slate-400 text-sm max-w-sm mb-6">
            {currentLang === "en"
              ? "The document could not be retrieved from the server. Please check your internet connection or session."
              : "Documentul nu a putut fi preluat de pe server. Verifică conexiunea la internet sau sesiunea."}
          </p>
          <button
            onClick={() => navigateTo("register")}
            className="px-4 py-2 bg-slate-800 rounded-lg text-sm font-semibold hover:bg-slate-700 transition-colors"
          >
            {currentLang === "en" ? "Back to registration" : "Înapoi la înregistrare"}
          </button>
        </div>
      ) : (
        !loading &&
        data && (
          <main className="max-w-4xl mx-auto w-full flex-1 animate-fade-in">
            <div className="bg-[#0F172A]/80 backdrop-blur-xl p-6 md:p-10 rounded-[2.5rem] border border-slate-800 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-600/5 blur-3xl -z-10" />

              {/* Header */}
              <header className="mb-8 pb-6 border-b border-slate-800">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2 py-0.5 bg-emerald-600 text-[9px] font-black rounded uppercase tracking-tighter flex items-center gap-1">
                    <Scale size={10} /> {currentLang === "en" ? "Legal Agreement" : "Acord Legal"}
                  </span>
                  <span className="text-slate-500 font-mono text-[10px] tracking-widest flex items-center gap-1">
                    <Calendar size={10} />{" "}
                    {currentLang === "en" ? "Current version: " : "Versiune curentă: "}
                    {data.version || "N/A"}
                  </span>
                </div>

                <h1 className="text-3xl md:text-5xl font-black tracking-tighter mb-4 uppercase italic">
                  {data.documentTitle} <br />
                  <span className="text-slate-500">{data.documentSubtitle}</span>
                </h1>

                <p
                  className="text-slate-400 text-sm leading-relaxed italic bg-slate-950/40 p-4 rounded-xl border border-white/5"
                  dangerouslySetInnerHTML={{ __html: data.introText }}
                />
              </header>

              <div className="space-y-10">
                {/* DEFINITIONS */}
                {data.definitions && data.definitions.length > 0 && (
                  <section>
                    <h3 className="text-emerald-500 text-[10px] uppercase tracking-[0.3em] font-black mb-6 flex items-center gap-2">
                      <FileText size={14} />{" "}
                      {currentLang === "en" ? "Definitions & Services" : "Definiții și Servicii"}
                    </h3>

                    <div className="space-y-3">
                      {data.definitions.map(
                        (item: { label: string; text: string }, idx: number) => (
                          <div
                            key={idx}
                            className="flex flex-col sm:flex-row sm:items-start justify-between py-3 border-b border-white/5 group hover:bg-white/[0.01] px-2 transition-colors gap-1 sm:gap-4"
                          >
                            <span className="text-white font-mono text-sm uppercase min-w-[180px] shrink-0">
                              {item.label}:
                            </span>
                            <span className="text-slate-400 text-xs sm:text-sm text-left">
                              {item.text}
                            </span>
                          </div>
                        )
                      )}
                    </div>
                  </section>
                )}

                {/* SECTIONS */}
                {data.sections &&
                  data.sections.map((sec: any) => {
                    // GDPR Header
                    if (sec.type === "gdpr_header") {
                      return (
                        <div key={sec.id} className="pt-8 border-t-2 border-slate-800">
                          <h2 className="text-xl md:text-2xl font-black tracking-tight text-white uppercase mb-2 flex items-center gap-2">
                            <Shield className="text-emerald-500" size={20} /> {sec.title}
                          </h2>
                          {sec.subtitle && (
                            <p className="text-slate-400 text-xs md:text-sm leading-relaxed italic">
                              {sec.subtitle}
                            </p>
                          )}
                        </div>
                      );
                    }

                    // GDPR Protection Section
                    if (sec.type === "gdpr_protection") {
                      return (
                        <section key={sec.id} className="pt-4">
                          <h3 className="text-emerald-500 text-[10px] uppercase tracking-[0.3em] font-black mb-4 flex items-center gap-2">
                            <IconMapper name={sec.icon} size={14} /> {sec.title}
                          </h3>
                          {sec.description && (
                            <p className="text-slate-400 text-xs md:text-sm leading-relaxed mb-4">
                              {sec.description}
                            </p>
                          )}
                          {sec.subsections && (
                            <div className="space-y-4 pl-2 border-l border-emerald-500/30">
                              {sec.subsections.map((sub: any, subIdx: number) => (
                                <div key={subIdx}>
                                  <h4 className="text-white font-mono text-xs uppercase tracking-wider mb-2">
                                    {sub.title}
                                  </h4>
                                  <p
                                    className="text-slate-400 text-xs md:text-sm leading-relaxed"
                                    dangerouslySetInnerHTML={{ __html: sub.text }}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </section>
                      );
                    }

                    // Standard, Business Model, or List Sections
                    return (
                      <section key={sec.id} className="pt-6 border-t border-slate-800">
                        <h3 className="text-emerald-500 text-[10px] uppercase tracking-[0.3em] font-black mb-4 flex items-center gap-2">
                          <IconMapper name={sec.icon} size={14} /> {sec.title}
                        </h3>

                        {sec.description && (
                          <p className="text-slate-300 text-sm leading-relaxed mb-4">
                            {sec.description}
                          </p>
                        )}

                        {/* Cards (if any, e.g. Business Model Section) */}
                        {sec.cards && sec.cards.length > 0 && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            {sec.cards.map((card: any, cIdx: number) => (
                              <div
                                key={cIdx}
                                className="bg-slate-950/40 p-4 rounded-xl border border-white/5"
                              >
                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                                  {card.title}
                                </p>
                                <p className="text-3xl font-black text-white italic">
                                  {card.value}
                                </p>
                                <p className="text-slate-400 text-[11px] mt-1">{card.sub}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Paragraphs (if any) */}
                        {sec.paragraphs && (
                          <div className="space-y-4 text-slate-400 text-xs md:text-sm leading-relaxed">
                            {sec.paragraphs.map((p: string, pIdx: number) => (
                              <p key={pIdx} dangerouslySetInnerHTML={{ __html: p }} />
                            ))}
                          </div>
                        )}

                        {/* List Items (if any, e.g. Cancellations Section) */}
                        {sec.listItems && (
                          <div className="space-y-4 text-slate-400 text-xs md:text-sm leading-relaxed list-none pl-0">
                            {sec.listItems.map((item: string, lIdx: number) => (
                              <div key={lIdx} className="flex gap-2 items-start">
                                <span className="text-emerald-500 mt-1.5 shrink-0">●</span>
                                <p dangerouslySetInnerHTML={{ __html: item }} />
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Highlight Box (if any) */}
                        {sec.highlightBox && (
                          <div
                            className="p-4 bg-emerald-950/20 rounded-xl border border-emerald-800/30 text-slate-300 mt-4"
                            dangerouslySetInnerHTML={{ __html: sec.highlightBox }}
                          />
                        )}
                      </section>
                    );
                  })}

                {/* ACCEPTANCE BLOCK */}
                {data.acceptBlock && (
                  <section className="pt-6 mt-6 border-t border-emerald-500/30 bg-emerald-950/10 p-4 rounded-xl border border-white/5">
                    <p
                      className="text-slate-300 text-xs md:text-sm leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: data.acceptBlock }}
                    />
                  </section>
                )}
              </div>
            </div>
          </main>
        )
      )}
    </div>
  );
};

export default TermsConditions;